import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useGatewayQuery } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import {
  UseQueryExtTData,
  UseQueryExtTVariables,
} from "@clockwise/web-commons/src/util/apollo.util";
import { keys } from "lodash";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

// Unused but don't delete!
// We want to use this one instead but
//  `useLazyQuery` calls multiple times and isn't fixed until Apollo Client 3.6.2
// https://github.com/apollographql/apollo-client/pull/9684/files
// use this istead of `SingleUseQueryExtListener` when the pipedream of Apollo Client 3.6.2 get to here
export const SingleUseLazyQueryExtListener = ({
  options,
  query,
  queryId,
  queryCallId,
}: {
  options?: UseQueryExtTVariables;
  query: UseQueryExtTData;
  queryId: string;
  queryCallId: number;
}) => {
  // Inferred Types don't work here, but we don't need it to!
  const [_query, { data, error, loading }] = useLazyQuery(query, options);

  useEffect(() => {
    try {
      if (options?.skip) {
        // useLazyQuery doesn't take a `skip` options
        // Thus we implement manually here
        return;
      }

      // Ughhh useLazyQuery calls multiple times and isn't fixed until Apollo Client 3.6.2
      // https://github.com/apollographql/apollo-client/pull/9684/files
      _query();
    } catch (errorResponse) {
      PostMessageManager.sendFetchApolloQuery(queryId, {
        data,
        loading: false,
        error: errorResponse,
      });
    }
  }, [JSON.stringify(query), JSON.stringify(options), queryId, queryCallId]);

  useEffect(() => {
    PostMessageManager.sendFetchApolloQuery(queryId, { data, loading, error });
  }, [queryId, data, loading, error]);

  return null;
};

export const SingleUseQueryExtListener = ({
  options,
  query,
  queryId,
  queryCallId,
}: {
  options?: UseQueryExtTVariables;
  query: UseQueryExtTData;
  queryId: string;
  queryCallId: number;
}) => {
  // Inferred Types don't work here, but we don't need it to!

  // UGH useQuery also has some weird multiple HTTP request issues : (
  // see: https://github.com/apollographql/apollo-client/issues/8518
  const { data, error, loading, refetch } = useQuery(query, options);

  useEffect(() => {
    if (queryCallId == 1) {
      // Don't refetch on the first call
      return;
    }

    try {
      const skip = options?.skip || false;
      if (skip) {
        // useLazyQuery doesn't take a `skip` options
        // Thus we implement manually here
        return;
      }

      refetch();
    } catch (errorResponse) {
      PostMessageManager.sendFetchApolloQuery(queryId, {
        data,
        loading: false,
        error: errorResponse,
      });
    }
  }, [JSON.stringify(query), JSON.stringify(options), queryId, queryCallId]);

  useEffect(() => {
    PostMessageManager.sendFetchApolloQuery(queryId, { data, loading, error });
  }, [queryId, data, loading, error]);

  return null;
};

export const SingleUseGatewayQueryExtListener = ({
  options,
  query,
  queryId,
  queryCallId,
}: {
  options?: UseQueryExtTVariables;
  query: UseQueryExtTData;
  queryId: string;
  queryCallId: number;
}) => {
  // Inferred Types don't work here, but we don't need it to!

  // UGH useQuery also has some weird multiple HTTP request issues : (
  // see: https://github.com/apollographql/apollo-client/issues/8518
  const { data, error, loading, refetch } = useGatewayQuery(query, options);

  useEffect(() => {
    if (queryCallId == 1) {
      // Don't refetch on the first call
      return;
    }

    try {
      const skip = options?.skip || false;
      if (skip) {
        // useLazyQuery doesn't take a `skip` options
        // Thus we implement manually here
        return;
      }

      refetch();
    } catch (errorResponse) {
      PostMessageManager.sendFetchApolloQuery(queryId, {
        data,
        loading: false,
        error: errorResponse,
      });
    }
  }, [JSON.stringify(query), JSON.stringify(options), queryId, queryCallId]);

  useEffect(() => {
    PostMessageManager.sendFetchApolloQuery(queryId, { data, loading, error });
  }, [queryId, data, loading, error]);

  return null;
};

const UseQueryExtListener = () => {
  const activeQueryCollection = useSelector(
    (state: IReduxState) => state.webExtension.activeQueryCollection,
  );

  return (
    <div id="use-query-ext-listener">
      {keys(activeQueryCollection).map((queryId) => {
        const { options, query, queryCallId, type } = activeQueryCollection[queryId];
        const queryName = query?.definitions?.[0]?.name?.value;

        // Both `useQuery` and `useLazyQuery` have crazy multiple HTTP request bugs in Apollo
        // ... `useQuery` is a little better, when using `fetchPolicy: "no-cache"`
        // But `useLazyQuery` is a little better in general
        // I need "no-cache" to work the best Thus we're using `useQuery`
        return type === "gateway" ? (
          <SingleUseGatewayQueryExtListener
            options={options}
            query={query}
            queryId={queryId}
            key={`${queryName}${queryId}`}
            queryCallId={queryCallId}
          />
        ) : (
          <SingleUseQueryExtListener
            options={options}
            query={query}
            queryId={queryId}
            key={`${queryName}${queryId}`}
            queryCallId={queryCallId}
          />
        );
      })}
    </div>
  );
};

export default UseQueryExtListener;
